import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { DatumScope } from "./datumScopeTypes";
import type {
  FoundationDatumVerticalAnchor,
  HorizontalJustification,
  LateralDatumJustification,
  LevelDatumAnchor,
  VerticalJustification,
  WallHorizontalDatum,
  WallLateralDatumAnchor,
  WallVerticalDatumAnchor,
} from "./datumsTypesEnum";

/**
 * @enum Utility enum defining the datum type. Will be extended in the future
 * @member LEVEL legacy level datum
 * @member FOUNDATION legacyfoundation datum
 * @member WALL legacy wall datum
 * @member WALL_PLANE horizontal datum on a wall plane (U position along planeHash)
 * @member SHAREABLE shareable datum (new datum types)
 */
export enum DatumType {
  LEVEL = "LEVEL",
  FOUNDATION = "FOUNDATION",
  WALL = "WALL",
  WALL_PLANE = "WALL_PLANE",
  STACKED_LEVEL = "STACKED_LEVEL",
  SHAREABLE = "SHAREABLE",
}

/**
 * @enum Utility enum for determining the axis of a DatumEntity
 * @member VERTICAL vertical axis, perpendicular to the ground plane
 * @member HORIZONTAL parallel to the ground plane and used for defining domains inside a building entity
 * @member LATERAL parallel to the ground plane and used for defining layers for a building entity
 */
export enum DatumDirection {
  VERTICAL = "VERTICAL",
  HORIZONTAL = "HORIZONTAL",
  LATERAL = "LATERAL",
}

/**
 * @enum Utility enum defining how a DatumEntity is resolved
 * @member ABSOLUTE absolute offset value
 * @member RELATIVE the offset value is relative to another datum
 */
export enum DatumOffsetType {
  ABSOLUTE = "ABSOLUTE",
  RELATIVE = "RELATIVE",
}

export interface DatumEntityBaseType extends DocEntityBase<"DATUM"> {
  readonly id: PrimaryEntityId<"DATUM">;
  readonly datumType: DatumType;
  readonly direction: DatumDirection;
  readonly offsetNormalized?: boolean;
  readonly offset?: number;
}

export interface DatumEntityBase<T, Q> extends DatumEntityBaseType {
  readonly offsetType: T;
  readonly justification: Q;
}

/**
 * Provides a way to identify the datums catalog settings where the datum was originally stored
 * If the datum is created in the new centralized table, alls the fields will be undefined
 */
export type LegacyDatumSourceSettings = {
  readonly srcSettingsId?: string;
  readonly srcSettingsSetId?: string;
  readonly srcSettingsName?: string;
  readonly srcSettingsSetName?: string;
};

/**
 * Vertical Datum Entity. Used for defining vertical position or extent for a building entity
 * An example of a vertical datum entity is a level datum or the vertical extends of a facade span
 */
export interface VerticalDatumEntity<T> extends DatumEntityBase<T, VerticalJustification> {
  readonly direction: DatumDirection.VERTICAL;
}
/**
 * Horizontal Datum Entity. Used for defining domains along a building entity
 * This can be used for example, for the placement of prop along a wall edge
 */
export interface HorizontalDatumEntity<T> extends DatumEntityBase<T, HorizontalJustification> {
  readonly direction: DatumDirection.HORIZONTAL;
}

/**
 * Lateral Datum Entity. Used for applying offsets to the inside or outside of a building entity
 * This can be used for example, for defining the reference plane for the roof overhang calculation
 * We can measure the overhang from the framing, the sheathing, the cladding... Each one these
 * would be a different lateral datum
 */
export interface LateralDatumEntity<T> extends DatumEntityBase<T, LateralDatumJustification> {
  readonly direction: DatumDirection.LATERAL;
}

export interface LegacyLevelDatumEntity extends VerticalDatumEntity<DatumOffsetType.RELATIVE> {
  readonly datumType: DatumType.LEVEL;
  readonly anchor: LevelDatumAnchor;
  readonly levelId?: ForeignEntityId<"LEVEL">;
}

export interface LegacyFoundationVerticalDatumEntity
  extends VerticalDatumEntity<DatumOffsetType.RELATIVE>,
    LegacyDatumSourceSettings {
  readonly datumType: DatumType.FOUNDATION;
  readonly anchor: FoundationDatumVerticalAnchor;
}

export interface LegacyFoundationLateralDatumEntity
  extends LateralDatumEntity<DatumOffsetType.RELATIVE>,
    LegacyDatumSourceSettings {
  readonly datumType: DatumType.FOUNDATION;
  readonly anchor: WallLateralDatumAnchor;
}

export interface LegacyWallHorizontalDatumEntity
  extends HorizontalDatumEntity<DatumOffsetType.RELATIVE>,
    LegacyDatumSourceSettings {
  readonly datumType: DatumType.WALL;
  readonly anchor: WallHorizontalDatum;
}

export interface LegacyWallPlaneHorizontalDatumEntity extends HorizontalDatumEntity<DatumOffsetType.ABSOLUTE> {
  readonly datumType: DatumType.WALL_PLANE;
  readonly planeHash: string;
  readonly datumScope?: DatumScope;
}

export interface LegacyWallVerticalDatumEntity
  extends VerticalDatumEntity<DatumOffsetType.RELATIVE>,
    LegacyDatumSourceSettings {
  readonly datumType: DatumType.WALL;
  readonly anchor: WallVerticalDatumAnchor;
}

export interface StackedLevelDatumEntity extends VerticalDatumEntity<DatumOffsetType.RELATIVE> {
  readonly datumType: DatumType.STACKED_LEVEL;
}

export interface AbsoluteVerticalDatumEntity extends VerticalDatumEntity<DatumOffsetType.ABSOLUTE> {
  readonly datumType: DatumType.SHAREABLE;
  readonly datumScope?: DatumScope;
}

export type LegacyWallDatumEntity = LegacyWallVerticalDatumEntity | LegacyWallHorizontalDatumEntity;
export type LegacyFoundationDatumEntity = LegacyFoundationVerticalDatumEntity | LegacyFoundationLateralDatumEntity;

export type WallVerticalDatumEntity =
  | LegacyWallVerticalDatumEntity
  | LegacyLevelDatumEntity
  | AbsoluteVerticalDatumEntity;

export type WallHorizontalDatumEntity = LegacyWallHorizontalDatumEntity;
export type WallPlaneHorizontalDatumEntity = LegacyWallPlaneHorizontalDatumEntity;

export type FoundationVerticalDatumEntity = LegacyFoundationVerticalDatumEntity | AbsoluteVerticalDatumEntity;
export type FoundationLateralDatumEntity = LegacyFoundationLateralDatumEntity;

export type FoundationDatumEntity = FoundationVerticalDatumEntity | FoundationLateralDatumEntity;
export type WallDatumEntity = WallVerticalDatumEntity | WallHorizontalDatumEntity;
export type LevelDatumEntity = LegacyLevelDatumEntity | AbsoluteVerticalDatumEntity;
export type LevelPlacementDatumEntity = StackedLevelDatumEntity | AbsoluteVerticalDatumEntity;
export type ShareableDatumEntity = AbsoluteVerticalDatumEntity;

export type WallPlaneDatumEntity = WallPlaneHorizontalDatumEntity;

export type LegacyDatumEntity =
  | FoundationDatumEntity
  | WallDatumEntity
  | LevelDatumEntity
  | StackedLevelDatumEntity
  | WallPlaneDatumEntity;
export type NonLegacyDatumEntity = ShareableDatumEntity;
export type DatumEntity = LegacyDatumEntity | NonLegacyDatumEntity;

export function isLegacyDatum(d?: DatumEntity | null): d is LegacyDatumEntity {
  return (
    isLegacyLevelDatum(d) ||
    isLegacyFoundationDatum(d) ||
    isLegacyWallDatum(d) ||
    isStackedLevelDatum(d) ||
    isWallPlaneHorizontalDatum(d)
  );
}

export function isNonLegacyDatum(d?: DatumEntity | null): d is NonLegacyDatumEntity {
  return !isLegacyDatum(d);
}

/**
 * Foundation datums
 */
export function isLegacyFoundationVerticalDatum(d?: DatumEntity | null): d is LegacyFoundationVerticalDatumEntity {
  return d?.datumType === DatumType.FOUNDATION && d?.direction === DatumDirection.VERTICAL;
}

export function isLegacyFoundationLateralDatum(d?: DatumEntity | null): d is LegacyFoundationLateralDatumEntity {
  return d?.datumType === DatumType.FOUNDATION && d?.direction === DatumDirection.LATERAL;
}

export function isFoundationVerticalDatum(d?: DatumEntity | null): d is FoundationVerticalDatumEntity {
  return isLegacyFoundationVerticalDatum(d) || isAbsoluteVerticalDatum(d);
}

export function isFoundationLateralDatum(d?: DatumEntity | null): d is FoundationLateralDatumEntity {
  return isLegacyFoundationLateralDatum(d);
}

export function isLegacyFoundationDatum(d?: DatumEntity | null): d is LegacyFoundationDatumEntity {
  return isLegacyFoundationVerticalDatum(d) || isLegacyFoundationLateralDatum(d);
}

export function isFoundationDatum(d?: DatumEntity | null): d is FoundationDatumEntity {
  return isFoundationVerticalDatum(d) || isFoundationLateralDatum(d);
}

/**
 * Wall datums
 */
export function isLegacyWallVerticalDatum(d?: DatumEntity | null): d is LegacyWallVerticalDatumEntity {
  return d?.datumType === DatumType.WALL && d?.direction === DatumDirection.VERTICAL;
}

export function isLegacyWallHorizontalDatum(d?: DatumEntity | null): d is LegacyWallHorizontalDatumEntity {
  return d?.datumType === DatumType.WALL && d?.direction === DatumDirection.HORIZONTAL;
}

export function isWallVerticalDatum(d?: DatumEntity | null): d is WallVerticalDatumEntity {
  return isLegacyWallVerticalDatum(d) || isLegacyLevelDatum(d) || isAbsoluteVerticalDatum(d);
}

export function isWallHorizontalDatum(d?: DatumEntity | null): d is WallHorizontalDatumEntity {
  return isLegacyWallHorizontalDatum(d);
}

export function isLegacyWallDatum(d?: DatumEntity | null): d is LegacyWallDatumEntity {
  return isLegacyWallVerticalDatum(d) || isLegacyWallHorizontalDatum(d);
}

export function isWallDatum(d?: DatumEntity | null): d is WallDatumEntity {
  return isWallVerticalDatum(d) || isWallHorizontalDatum(d);
}

/**
 * Wall plane datums
 */
export function isWallPlaneHorizontalDatum(d?: DatumEntity | null): d is WallPlaneHorizontalDatumEntity {
  return d?.datumType === DatumType.WALL_PLANE && d?.direction === DatumDirection.HORIZONTAL;
}

export function isWallPlaneDatum(d?: DatumEntity | null): d is WallPlaneDatumEntity {
  return isWallPlaneHorizontalDatum(d);
}

/**
 * Level datums
 */
export function isLegacyLevelDatum(d?: DatumEntity | null): d is LegacyLevelDatumEntity {
  return d?.datumType === DatumType.LEVEL;
}

export function isLevelDatum(d?: DatumEntity | null): d is LevelDatumEntity {
  return isLegacyLevelDatum(d) || isAbsoluteVerticalDatum(d);
}

export function isDatumEntityValidForLevel(levelId: string | undefined, d?: DatumEntity | null): d is LevelDatumEntity {
  if (!isLevelDatum(d)) {
    return false;
  }

  if (isLegacyLevelDatum(d)) {
    return !!levelId && d.levelId === levelId;
  }

  return true;
}

/**
 * Stacked level datums
 */
export function isStackedLevelDatum(d?: DatumEntity | null): d is StackedLevelDatumEntity {
  return d?.datumType === DatumType.STACKED_LEVEL;
}

export function isLevelPlacementDatum(d?: DatumEntity | null): d is LevelPlacementDatumEntity {
  return isStackedLevelDatum(d) || isAbsoluteVerticalDatum(d);
}

/**
 * Shareable datums
 */
export function isShareableDatum(d?: DatumEntity | null): d is ShareableDatumEntity {
  return d?.datumType === DatumType.SHAREABLE;
}

export function isAbsoluteVerticalDatum(d?: DatumEntity | null): d is AbsoluteVerticalDatumEntity {
  return (
    d?.datumType === DatumType.SHAREABLE &&
    d?.direction === DatumDirection.VERTICAL &&
    d?.offsetType === DatumOffsetType.ABSOLUTE
  );
}
